'use client';
import React, { useRef, useEffect, useState } from 'react';
import type { RagNode, RagConnection, HierarchicalNode } from './ragCanvas.types';

// Canvas绘制相关常量
const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 300;
const BACKGROUND_COLOR = '#121212';
const NODE_COLOR = '#212121';
const HIGHLIGHT_COLOR = '#A772FF';
const ROOT_COLOR = '#4FFF00';
const LINE_COLOR = '#A772FF';
const GRID_COLOR = '#2A2A2A';

/**
 * Canvas绘制函数
 */
function drawCanvas(
  ctx: CanvasRenderingContext2D,
  nodes: RagNode[],
  connections: RagConnection[],
  presetConnections: RagConnection[],
  animationProgress: Map<string, number>,
  rootId: number | null,
  rootNode?: HierarchicalNode | null,
) {
  // 清除画布并填充背景
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  // 绘制预设连接（灰色网格线）
  presetConnections.forEach((conn: RagConnection) => {
    const fromNode = nodes[conn.from];
    const toNode = nodes[conn.to];
    if (!fromNode || !toNode) return;

    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    ctx.moveTo(fromNode.x, fromNode.y);
    ctx.lineTo(toNode.x, toNode.y);
    ctx.stroke();
  });

  ctx.globalAlpha = 1;

  // 绘制动画连接
  connections.forEach((conn: RagConnection) => {
    const fromNode = nodes[conn.from];
    const toNode = nodes[conn.to];
    if (!fromNode || !toNode) return;

    const key = `${conn.from}-${conn.to}`;
    const progress = animationProgress.get(key) || 0;

    if (progress > 0) {
      const midX = fromNode.x + (toNode.x - fromNode.x) * progress;
      const midY = fromNode.y + (toNode.y - fromNode.y) * progress;

      // 创建渐变
      const gradient = ctx.createLinearGradient(fromNode.x, fromNode.y, midX, midY);
      gradient.addColorStop(0, 'rgba(167, 114, 255, 0.3)');
      gradient.addColorStop(1, 'rgba(167, 114, 255, 1)');

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 1;
      ctx.shadowColor = LINE_COLOR;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.moveTo(fromNode.x, fromNode.y);
      ctx.lineTo(midX, midY);
      ctx.stroke();

      // 绘制移动的点
      if (progress < 1) {
        ctx.shadowColor = LINE_COLOR;
        ctx.shadowBlur = 15;
        ctx.fillStyle = LINE_COLOR;
        ctx.beginPath();
        ctx.arc(midX, midY, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.shadowBlur = 0;
    }
  });

  ctx.globalAlpha = 1;

  // 绘制节点
  nodes.forEach((node: RagNode) => {
    let color = NODE_COLOR;

    if (node.id === rootId) {
      color = ROOT_COLOR;
    } else if (node.isHighlighted) {
      color = HIGHLIGHT_COLOR;
    }

    if (node.id === rootId) {
      // 根节点：多层阴影效果
      ctx.shadowColor = ROOT_COLOR;
      ctx.shadowBlur = 25;
      ctx.globalAlpha = 1;
      ctx.fillStyle = color;
      for (let i = 0; i < 6; i++) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    } else if (node.isHighlighted) {
      // 高亮节点：多层阴影效果
      ctx.shadowColor = HIGHLIGHT_COLOR;
      ctx.shadowBlur = 20;
      ctx.globalAlpha = 1;
      ctx.fillStyle = color;
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    } else {
      // 普通节点
      ctx.shadowBlur = 0;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.shadowBlur = 0;

    // 绘制文本标签
    if (node.isHighlighted || node.id === rootId) {
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#B7B9BC';

      if (node.id === rootId) {
        ctx.fillText(rootNode?.label || '', node.x, node.y + node.radius + 4);
      } else if (node.name) {
        const words = node.name.replace('.md', '').split(' ');
        const lines = [];
        let currentLine = '';

        for (const word of words) {
          if (lines.length >= 2) break;
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          if (testLine.length <= 8) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              currentLine = word.slice(0, 8);
            } else {
              lines.push(word.slice(0, 8));
              currentLine = '';
            }
          }
        }

        if (currentLine && lines.length < 2) {
          lines.push(currentLine.slice(0, 8));
        }

        lines.forEach((line, index) => {
          ctx.fillText(line, node.x, node.y + node.radius + 4 + index * 11);
        });
      }
    }
  });
}

import type { RagCanvasProps } from './ragCanvas.types';

/**
 * RAG检索动画Canvas组件
 */
function RagCanvas({
  nodes = [],
  connections = [],
  presetConnections = [],
  animationProgress = new Map(),
  rootId = null,
  rootNode,
  onNodeHover = null,
  className = '',
}: RagCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredNode, setHoveredNode] = useState<HierarchicalNode | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // 绘制canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    drawCanvas(ctx, nodes, connections, presetConnections, animationProgress, rootId, rootNode);
  }, [nodes, connections, presetConnections, animationProgress, rootId, rootNode]);

  // 鼠标移动事件处理
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let found = false;
    for (const node of nodes) {
      if (node.isHighlighted) {
        const distance = Math.sqrt(Math.pow(x - node.x, 2) + Math.pow(y - node.y, 2));
        if (distance <= node.radius + 8) {
          if (node.id === rootId && rootNode) {
            setHoveredNode(rootNode);
            setTooltipPosition({ x: e.clientX, y: e.clientY });
            found = true;
            break;
          } else {
            // 从connections中找到to属性值与node.id相同的连接
            const connection = connections.find((conn) => conn.to === node.id);
            if (connection?.node) {
              setHoveredNode(connection.node);
              setTooltipPosition({ x: e.clientX, y: e.clientY });
              found = true;
              break;
            }
          }
        }
      }
    }

    if (!found) {
      setHoveredNode(null);
    }
  };

  const handleMouseLeave = () => {
    setHoveredNode(null);
  };

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className={`border border-gray-800 rounded-lg cursor-pointer ${className}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      {hoveredNode && (
        // eslint-disable-next-line react/forbid-dom-props
        <div
          className="fixed z-50 rounded-lg p-3 shadow-lg"
          style={{
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y + 15}px`,
            pointerEvents: 'none',
            backgroundColor: 'rgba(27, 27, 27, 0.85)',
          }}
        >
          <div className="flex flex-col gap-2 min-w-[200px]">
            <div className="font-medium text-white text-xs">{hoveredNode.label}</div>
            <div className="text-text-secondary text-xs">
              <span className="font-medium">Type:</span> {hoveredNode.type}
            </div>
            {hoveredNode.description && (
              <div className="text-text-secondary text-xs">{hoveredNode.description}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RagCanvas;
